import axios from 'axios';
import i18next from 'i18next';
import { queryClient } from '@hotjar/webapp/utils/react-query';
import { CURRENT_USER_QUERY_KEY } from '@hotjar/user';
import { getSiteId } from '../../../utils/getParamsFromUrlPathname';
import { getUTCOffset } from './getUTCOffset';

import type { Store } from '../../../store/list/types';
import { getFiltersMergedWithRelevanceFilter } from '@hotjar/recordings/src/utils/getFiltersMergedWithRelevanceFilter';
import { QueryListFilter } from '@hotjar/global-filters';
import { HotjarFilterOptionAll, HotjarFilterOptionWatched } from '../../../store/list/types';
import { convertSortByV2ToV3 } from '@hotjar/recordings/src/features/RecordingsPage/utils/convertSortByV2ToV3';

const API_ROOT_V3 = '/api/v3/sites/';

export const exportRecordings = (store: Store, format: string): Promise<void> => {
  const { showExportBanner, setShowExportBanner, setExportBannerMessage, setExportBannerKind } =
    store.getState();

  const genericError = i18next.t('common:recordings.export-recordings.error');

  // In case the banner is already open
  if (showExportBanner) {
    setShowExportBanner(false);
  }

  return sendRecordingsExport(store, format)
    .then(
      (success) => {
        if (success) {
          const user: { email: string } | undefined =
            queryClient.getQueryData(CURRENT_USER_QUERY_KEY);
          const email = user?.email;

          // eslint-disable-next-line max-len
          setExportBannerMessage(
            i18next.t('common:recordings.export-recordings.success', { email }),
          );
          setExportBannerKind('success');
        } else {
          setExportBannerMessage(genericError);
          setExportBannerKind('danger');
        }
      },
      () => {
        setExportBannerMessage(genericError);
        setExportBannerKind('danger');
      },
    )
    .finally(() => {
      setShowExportBanner(true);
    });
};

/**
 * Initiates an export of the Recordings List by contacting the API.
 *
 * This API endpoint (v3 prefix in this case) hits Clickhouse.
 * At a later time, the user will receive an email with the link to download their export.
 *
 * API Docs
 * https://insights-integration.live.eks.hotjar.com/autodocs/redoc/#tag/Recordings/operation/post__api_v3_sites_%7Bsite_id%7D_recordings_export
 *
 * @param store An instance of the Recordings List store.
 * @param format The desired format of the export: "csv" or "xlsx".
 * @returns bool Whether the export has been successfully initiated.
 */
async function sendRecordingsExport(store: Store, format: string): Promise<boolean> {
  const { jsonFilters, hotjarFilters, sortBy } = store.getState();

  const response = await axios.post(`${API_ROOT_V3}${getSiteId()}/recordings/export`, {
    format,
    global_filters: JSON.stringify(
      getFiltersMergedWithRelevanceFilter(jsonFilters as QueryListFilter, hotjarFilters),
    ),
    ...(hotjarFilters.watched !== HotjarFilterOptionAll && {
      watched: hotjarFilters.watched === HotjarFilterOptionWatched,
    }),
    sort_by: convertSortByV2ToV3(sortBy),
    tz_offset: getUTCOffset(),
  });

  return response.status === 200;
}
